import { useState, useEffect, useRef } from "react";

export function GifPicker({ onSelect, onClose }) {
  const [query,    setQuery]    = useState('');
  const [gifs,     setGifs]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [tab,      setTab]      = useState('trending'); // 'trending' | 'search'
  const debounceRef = useRef(null);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Load trending on mount
  useEffect(() => { fetchTrending(); }, []);

  // Search as user types
  useEffect(() => {
    if (!query.trim()) { fetchTrending(); return; }
    setTab('search');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSearch(query), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const fetchTrending = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/gifs/trending?limit=24');
      const d = await res.json();
      setGifs(d.gifs || []);
      setTab('trending');
    } catch { setGifs([]); }
    setLoading(false);
  };

  const fetchSearch = async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/gifs/search?q=${encodeURIComponent(q)}&limit=24`);
      const d = await res.json();
      setGifs(d.gifs || []);
    } catch { setGifs([]); }
    setLoading(false);
  };

  return (
    <div ref={ref} style={{
      position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
      width: 340, maxHeight: 400,
      background: 'var(--bg-secondary, #1e1f2e)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      display: 'flex', flexDirection: 'column', zIndex: 200, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search GIFs…"
            style={{
              flex: 1, padding: '6px 10px', borderRadius: 8,
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', fontSize: 13, outline: 'none',
            }}
          />
          {query && (
            <button onClick={() => setQuery('')}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 14, cursor: 'pointer', padding: '0 4px' }}>
              ✕
            </button>
          )}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 5, textAlign: 'right' }}>
          Powered by Tenor
        </div>
      </div>

      {/* Section label */}
      <div style={{ padding: '6px 12px 4px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
        {tab === 'trending' ? '🔥 Trending' : `Results for "${query}"`}
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
            Loading…
          </div>
        ) : gifs.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
            No GIFs found
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, paddingTop: 4 }}>
            {gifs.map(gif => (
              <button
                key={gif.id}
                onClick={() => { onSelect(gif); onClose(); }}
                style={{ padding: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', aspectRatio: '1', transition: 'transform 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                title={gif.title}
              >
                <img src={gif.preview || gif.url} alt={gif.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
